import { SolitaireGame } from "./SolitaireGame";

/**
 * MenuManager - Handles menu UI interactions and background selection
 * Low coupling: Independent class that manages HTML menu elements
 */
export class MenuManager {
  private menuButton: HTMLElement | null;
  private menuPanel: HTMLElement | null;
  private newGameButton: HTMLElement | null;
  private backgroundOptions: NodeListOf<Element>;
  private game: SolitaireGame;
  private currentBackground: string = 'matrix';

  constructor(game: SolitaireGame) {
    this.game = game;
    
    // Get HTML elements
    this.menuButton = document.getElementById("menu-btn");
    this.menuPanel = document.getElementById("menu-panel");
    this.newGameButton = document.getElementById("new-game-btn");
    this.backgroundOptions = document.querySelectorAll(".background-option");

    if (!this.menuButton || !this.menuPanel || !this.newGameButton) {
      console.warn("Menu elements not found");
      return;
    }

    // Ensure visual state matches default background (matrix)
    // GameBoard already creates matrix background by default
    this.currentBackground = 'matrix';

    this.setupMenu();
  }

  private setupMenu(): void {
    if (!this.menuButton || !this.menuPanel || !this.newGameButton) {
      return;
    }

    // Toggle menu
    this.menuButton.addEventListener("click", (e) => {
      e.stopPropagation();
      this.toggleMenu();
    });

    // Close menu when clicking outside
    document.addEventListener("click", (e) => {
      if (this.menuPanel && this.menuButton && 
          !this.menuPanel.contains(e.target as Node) && 
          !this.menuButton.contains(e.target as Node)) {
        this.closeMenu();
      }
    });

    // Handle new game button
    this.newGameButton.addEventListener("click", () => {
      this.onNewGameClick();
    });

    // Handle background selection
    this.backgroundOptions.forEach((option) => {
      option.addEventListener("click", () => {
        const bgType = (option as HTMLElement).dataset.bg;
        if (bgType) {
          this.selectBackground(bgType);
        }
      });
    });
  }

  /**
   * Toggle menu open/closed state
   */
  toggleMenu(): void {
    if (!this.menuButton || !this.menuPanel) return;
    
    this.menuButton.classList.toggle("active");
    this.menuPanel.classList.toggle("active");
  }

  /**
   * Close the menu
   */
  closeMenu(): void {
    if (!this.menuButton || !this.menuPanel) return;
    
    this.menuButton.classList.remove("active");
    this.menuPanel.classList.remove("active");
  }

  /**
   * Handle new game button click
   */
  private onNewGameClick(): void {
    // Trigger new game handler (set by UIManager)
    const handler = (this.newGameButton as any).onclick;
    if (handler) {
      handler();
    }
    
    // Close menu after starting new game
    this.closeMenu();
  }

  /**
   * Select a background color scheme
   */
  selectBackground(colorScheme: string): void {
    if (this.currentBackground === colorScheme) return;
    
    this.currentBackground = colorScheme;
    
    // Update visual selection
    this.backgroundOptions.forEach(opt => opt.classList.remove("active"));
    const selectedOption = Array.from(this.backgroundOptions).find(
      opt => (opt as HTMLElement).dataset.bg === colorScheme
    );
    if (selectedOption) {
      selectedOption.classList.add("active");
    }
    
    // Change game board background
    this.game.changeBackground(colorScheme);
  }

  /**
   * Set the new game button handler
   */
  setNewGameHandler(handler: () => void): void {
    if (this.newGameButton) {
      this.newGameButton.onclick = handler;
    }
  }
}

